import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { DMSImportIcon } from 'assets/icons';
import B2BComponent from 'components/B2BComponents/B2BComponent';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import { DataContainer } from 'components/DataContainer';
import {
  getEstimateById,
  getEstimateVehicle,
  getIsOrderDialogOpen,
  setOrderDialogOpen,
} from 'domains/estimate/Estimate.store';
import { GarageView, getSparePartsView } from 'domains/user';
import ClientSection from 'pages/EstimatePage/ClientSection';
import DetailsSection from 'pages/EstimatePage/DetailsSection';
import { EstimateTableSection } from 'pages/EstimatePage/EstimatePage.styled';
import ObservationSection from 'pages/EstimatePage/ObservationSection';
import B2BEstimateOrderDialog from 'pages/EstimatePage/OrderEstimate/B2BEstimateOrderDialog';
import EstimateOperations from 'pages/EstimatePage/TableSection/Operations/EstimateOperations';
import TotalSection from 'pages/EstimatePage/TotalSection';
import TotalSectionB2B from 'pages/EstimatePage/TotalSection/TotalSectionB2B';
import VehicleSection from 'pages/EstimatePage/VehicleSection';
import { theme } from 'styles';
import { CenteredSpin, Flex, Icon, MarginBox, notifyTop, Text } from 'UI';
import ViewTabs from 'UI/Tabs/ViewTabs';
import { hasData, useBreakpointSelector } from 'utils';
import ClientSectionB2B from './ClientSection/ClientSectionB2B';
import DealerSection from './DealerSection';

const EMPTY_ESTIMATE_HEIGHT = 2400;

export interface EstimateModifyTabProps {
  estimateId: string;
}

export interface PdfFilterProps {
  hideReferenceNumbers: boolean;
  hideDiscounts: boolean;
}

const EstimateModifyTab = ({ estimateId }: EstimateModifyTabProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const breakpointSelector = useBreakpointSelector();
  const sparePartsViewStore = useSelector(getSparePartsView);
  const isB2B = useIsB2B();
  const sparePartsView = isB2B ? GarageView : sparePartsViewStore;
  const isOrderEstimateOpen = useSelector(getIsOrderDialogOpen);

  const setOpenOrderDialog = (val: boolean) => dispatch(setOrderDialogOpen(isB2B ? val : false));

  const vehicle = useSelector((state: RootState) => getEstimateVehicle(state, estimateId));
  const estimate = useSelector((state: RootState) => getEstimateById(state, estimateId));

  if (hasData(estimate) && estimate.isHidden) {
    notifyTop(
      'warning',
      <Trans i18nKey={'estimate.deleted'}>
        This estimate has been deleted by another user. Please select a different estimate.
      </Trans>,
      undefined,
      undefined,
    );
  }

  return (
    <DataContainer
      data={estimate}
      Loading={() => (
        <Flex minHeight={EMPTY_ESTIMATE_HEIGHT}>
          <CenteredSpin />
        </Flex>
      )}
    >
      <B2BEstimateOrderDialog
        isOpen={isOrderEstimateOpen}
        setOrderEstimateOpen={setOpenOrderDialog}
        estimateId={estimateId}
      />
      {hasData(estimate) && estimate.dmsOrderNumber && (
        <Flex justify={'flex-end'}>
          <MarginBox mt={40} mr={30}>
            <Flex>
              <Icon IconComponent={DMSImportIcon} size={29} mr={10} color={theme.color.blue_dark_2} noPointer />
              <Text type="base" bold>
                {t('estimate.dms.repair_order_number', 'Repair order number:')}
              </Text>
              <MarginBox mr={5} />
              <Text type="base">{estimate.dmsOrderNumber}</Text>
            </Flex>
          </MarginBox>
        </Flex>
      )}
      <Flex direction={breakpointSelector(['column', 'row'])} justify={'flex-start'} gap={20}>
        <B2BComponent
          b2c={
            <>
              {vehicle && (
                <Flex size={5} direction={'column'}>
                  <VehicleSection vehicle={vehicle} />
                </Flex>
              )}
            </>
          }
        />
        <Flex size={3} direction={'column'}>
          <B2BComponent
            b2b={<ClientSectionB2B estimateId={estimateId} />}
            b2c={<ClientSection estimateId={estimateId} />}
            data-cy={'client-contact-section'}
          />
        </Flex>
        <Flex size={3} direction={'column'}>
          <B2BComponent b2c={<DealerSection />} />
        </Flex>
      </Flex>
      <MarginBox mt={15} />
      <B2BComponent b2c={<ViewTabs margin="0" />} />
      <Flex direction={'column'} data-cy={'estimate-form'} gap={15}>
        <EstimateTableSection
          type={'reference'}
          estimateId={estimateId}
          sparePartsView={sparePartsView}
          data-cy={'references-section'}
          collapsible
        />
        <B2BComponent
          b2c={
            <Flex direction={'column'} gap={15}>
              <EstimateTableSection
                type={'labor_time'}
                estimateId={estimateId}
                data-cy={'labor-time-section'}
                collapsible
              />
              <EstimateTableSection
                type={'bundle_or_recycling'}
                estimateId={estimateId}
                data-cy={'bundle-or-recycling-section'}
                collapsible
              />
            </Flex>
          }
        />
        <B2BComponent b2c={<EstimateOperations estimateId={estimateId} />} />
        <Flex direction={breakpointSelector(['column', 'row'])}>
          <Flex size={3} direction={'column'} data-cy={'observation-section'}>
            <ObservationSection key={estimateId} estimateId={estimateId} />
          </Flex>
          <MarginBox ml={breakpointSelector([0, 30])} mt={breakpointSelector([30, 0])} />
          <Flex size={3} direction={'column'} data-cy={'order-details-section'}>
            <DetailsSection estimateId={estimateId} sparePartsView={sparePartsView} />
          </Flex>
          <MarginBox ml={breakpointSelector([0, 30])} mt={breakpointSelector([30, 0])} />
          <Flex size={2} direction={'column'} data-cy={'total-section'}>
            <B2BComponent
              b2c={<TotalSection estimateId={estimateId} />}
              b2b={<TotalSectionB2B estimateId={estimateId} />}
            />
          </Flex>
        </Flex>
        <MarginBox mt={30} />
      </Flex>
    </DataContainer>
  );
};

export default EstimateModifyTab;
